import { ContentThumbnail, Skeleton } from "@/components";
import { useAppProperties, useNavigateToItem } from "@/hooks";
import { useUpNext } from "../hooks/useUpNext.tsx";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { cn } from "@/utils";
import { CourseOverview } from "@/app/modules/CollectionDetails";

interface UpNextParams {
  currentItemId: number;
  isSidePanel?: boolean;
}

export const UpNext = ({ currentItemId, isSidePanel }: UpNextParams) => {
  const { navigateToItem } = useNavigateToItem();
  const { translate } = useTranslate();
  const { upNextLabel } = useAppProperties();
  const { drawerWidth } = useResponsiveness();

  const {
    nextItems,
    getFullItem,
    collectionId,
    isLoading,
    upNextCollection,
    courseId,
  } = useUpNext({
    itemId: currentItemId,
  });

  const itemsToShow = nextItems.slice(0, 20);

  const isEmpty = !isLoading && itemsToShow.length === 0 && !courseId;

  return (
    <div
      className={cn(isSidePanel ? "shrink-0" : "")}
      style={isSidePanel ? { width: drawerWidth } : {}}
    >
      {/* Negative margins are to have the highlight background on the up next items leak outside the UpNext column*/}
      {!isEmpty && (
        <div
          className={cn("mb-10 ml-[-12px]", isSidePanel ? "pt-0" : "pt-[52px]")}
        >
          {courseId ? (
            <div className="pr-7">
              {upNextCollection && (
                <CourseOverview className="ml-[12px]" collection={upNextCollection} isSidePanel={isSidePanel}/>
              )}
            </div>
          ) : (
            <>
              <div className="h5-bold pl-3">{translate(upNextLabel)}</div>
              <div className="flex flex-col pl-3 pt-2">
                {isLoading
                  ? [1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="my-3 flex h-[89px] w-full items-center gap-4"
                      >
                        <Skeleton className="h-full w-[159px] shrink-0 rounded-lg" />
                        <Skeleton className="mr-20 h-5 grow" />
                      </div>
                    ))
                  : itemsToShow.map((i, index) => {
                      const fullItem = getFullItem(i);

                      return (
                        <div
                          key={`${index}|${i.ChildId}`}
                          className="ml-[-12px] flex h-[113px] cursor-pointer items-center rounded-lg  p-3 hover:bg-surface-secondary"
                          onClick={() =>
                            navigateToItem({
                              item: i,
                              parentId: collectionId,
                              fullItem,
                            })
                          }
                        >
                          <ContentThumbnail
                            thumbnail={i.Thumbnail}
                            contentItem={fullItem}
                            className="aspect-video h-full w-[159px] flex-shrink-0 rounded-lg"
                          />
                          <p className="p1-semibold line-clamp-3 overflow-hidden overflow-ellipsis p-4">
                            {i.Title}
                          </p>
                        </div>
                      );
                    })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
